import React, { useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import ExportIcon from '@mui/icons-material/ImportExport';
import { selectSelectedViewAllChart } from '../../../../redux/slices/summaryGraphViewSlice';
import { CHART_COMPONENTS, CHART_TITLES, type ChartType } from '../Charts/ChartTypes';

interface ViewAllChartProps {
	open: boolean;
	onClose: () => void;
}

const ViewAllChart: React.FC<ViewAllChartProps> = ({ open, onClose }) => {
	const selectedChart: ChartType | null = useSelector((state: any) => selectSelectedViewAllChart(state));
	const exportFnRef = useRef<(() => void) | null>(null);

	// Register export function from chart
	const registerExportFn = (fn: () => void) => {
		exportFnRef.current = fn;
	};

	const handleExport = () => {
		if (exportFnRef.current) {
			exportFnRef.current();
		} else {
			// Optionally show a message if export is not available
			console.warn('Export function not registered for this chart.');
		}
	};

	const renderChart = () => {
		if (!selectedChart) return null;
		const ChartComponent = CHART_COMPONENTS[selectedChart];
		// Pass registerExportFn to chart if supported
		return ChartComponent ? <ChartComponent fullWidth registerExportFn={registerExportFn} /> : null;
	};

	const chartTitle = CHART_TITLES[selectedChart!] || 'Chart';

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="lg"
			fullWidth
			aria-labelledby="view-all-chart-dialog"
		>
			<DialogTitle>
				<Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
					{/* Title left, icons right */}
					<Typography variant="h6" sx={{ flex: 1, textAlign: 'left' }}>{chartTitle}</Typography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }} onClick={handleExport}>
							<ExportIcon fontSize="small" />
							<Typography variant="body1" component="span">
								Export
							</Typography>
						</Box>
						<IconButton
							aria-label="close"
							data-testid="view-all-chart-close-button"
							onClick={onClose}
							sx={{ color: (theme) => theme.palette.grey[500] }}
						>
							<CloseIcon />
						</IconButton>
					</Box>
				</Box>
			</DialogTitle>
			<DialogContent dividers>
				<Box
					sx={{
						minHeight: '500px',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
					}}
				>
					{renderChart()}
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} color="primary" data-testid="view-all-chart-dialog-close-button">
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ViewAllChart;
